/**
 * PriorConduct - Prosecutor-friendly inspection history
 *
 * Redesigned to match UnderstaffingSummary pattern:
 * - Comparison bar charts (This Facility vs Sweetwater vs NJ vs State Avg)
 * - Focus on: Citations, Patient Harm, Staffing F-Tags, Fines
 * - Clear narrative callouts when facility is worse than prosecution baselines
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import api from '../../api/axios';
import MonthlyTimeline from './MonthlyTimeline';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

// Citation benchmark data by size bucket
interface SizeBucketBenchmark {
  bed_range: string;
  num_facilities: number;
  avg: number;
  median: number;
  p25: number;
  p75: number;
  p90: number;
  avg_harm: number;
  median_harm: number;
  avg_jeopardy: number;
  avg_staffing_ftags: number;
  // F-tag category benchmarks
  avg_abuse_neglect_ftags?: number;
  avg_quality_of_care_ftags?: number;
  avg_infection_control_ftags?: number;
  avg_pharmacy_ftags?: number;
  avg_behavioral_health_ftags?: number;
  avg_dietary_ftags?: number;
  // Summed totals (2019-2025)
  sum_avg?: number;
  sum_median?: number;
  sum_p25?: number;
  sum_p75?: number;
  sum_p90?: number;
  sum_avg_harm?: number;
  sum_years?: string;
}

interface PriorConductProps {
  ccn: string;
  schemeId?: string;
  facilityYears?: any[];
  state: string;
  beds?: number;
  startYear?: number;
  endYear?: number;
  facilityMetrics?: {
    total_harm_citations?: number | null;
    total_jeopardy_citations?: number | null;
    harm_per_100_residents?: number | null;
    total_citations_per_100?: number | null;
    total_citations?: number | null;
    total_fine_amount?: number | null;
    staffing_ftags_total?: number | null;
    staffing_harm_total?: number | null;
    // F-tag category totals (2020-2024)
    total_staffing_ftags?: number | null;
    total_abuse_neglect_ftags?: number | null;
    total_quality_of_care_ftags?: number | null;
    total_infection_control_ftags?: number | null;
    total_pharmacy_ftags?: number | null;
    total_behavioral_health_ftags?: number | null;
    total_dietary_ftags?: number | null;
  };
  citationBenchmarks?: Record<string, Record<string, SizeBucketBenchmark>> | null;
}

interface TimelineEvent {
  id: string;
  date: string;
  year: number;
  type: 'health' | 'complaint' | 'penalty' | 'sff';
  summary: string;
  citations?: number;
  harm_citations?: number;
  jeopardy_citations?: number;
  fine_amount?: number;
  details?: {
    ftag: string;
    description: string;
    severity: string;
    is_core: boolean;
  }[];
}

// ─────────────────────────────────────────────────────────────────────────────
// BASELINES - Prosecution case comparisons (per 100 residents for fair comparison)
// ─────────────────────────────────────────────────────────────────────────────

// Sweetwater Healthcare (CA) - 19 facilities, criminally prosecuted for understaffing
// Actual data from snf_understaffing_combined_facility_rollup and snf_penalty_features
const SWEETWATER_BASELINE = {
  citations_per_100: 22.4,  // Actual avg from facility rollup
  harm_per_100: 0.9,        // Actual avg from facility rollup
  total_fines: 167000,      // Actual avg per facility from snf_penalty_features
};

// Deptford Case - 2 facilities (CCNs: 315174, 315209), under investigation
// Actual data from snf_understaffing_combined_facility_rollup and snf_penalty_features
const NJ_BASELINE = {
  citations_per_100: 6.3,   // Actual avg from facility rollup
  harm_per_100: 0.4,        // Actual avg from facility rollup
  total_fines: 153000,      // Actual avg per facility from snf_penalty_features
};

// State averages from snf_understaffing_combined_facility_rollup and snf_penalty_features
// Citations are per 100 residents avg per year, fines are all-time per facility
const STATE_BASELINES: Record<string, {
  citations_per_100: number;
  harm_per_100: number;
  total_fines: number;
}> = {
  DEFAULT: { citations_per_100: 10.5, harm_per_100: 0.6, total_fines: 89000 },  // National avg
  CA: { citations_per_100: 15.4, harm_per_100: 0.9, total_fines: 67000 },
  NJ: { citations_per_100: 4.2, harm_per_100: 0.2, total_fines: 77000 },
  NY: { citations_per_100: 2.8, harm_per_100: 0.1, total_fines: 37000 },
  MA: { citations_per_100: 9.3, harm_per_100: 0.6, total_fines: 122000 },
  WA: { citations_per_100: 19.2, harm_per_100: 1.3, total_fines: 181000 },
};

// National percentiles (for context) - per 100 residents
const _NATIONAL_PERCENTILES = {
  citations_per_100: { p50: 6.4, p75: 15, p90: 30 },
  harm_per_100: { p50: 0, p75: 1.5, p90: 4 },
  fines: { p50: 0, p75: 7443, p90: 39468, p95: 71201 },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '$0';
  const absVal = Math.abs(val);
  if (absVal >= 1000000) return `$${(absVal / 1000000).toFixed(1)}M`;
  if (absVal >= 1000) return `$${Math.round(absVal / 1000)}K`;
  return `$${absVal.toLocaleString()}`;
};

/**
 * Determine facility size bucket based on bed count
 * Small: 0-60 beds, Medium: 61-120 beds, Large: 121+ beds
 */
const getSizeBucket = (beds: number | undefined | null): 'small' | 'medium' | 'large' => {
  if (!beds || beds <= 60) return 'small';
  if (beds <= 120) return 'medium';
  return 'large';
};

const getSizeBucketLabel = (bucket: 'small' | 'medium' | 'large'): string => {
  switch (bucket) {
    case 'small': return 'Small (0-60 beds)';
    case 'medium': return 'Medium (61-120 beds)';
    case 'large': return 'Large (121+ beds)';
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// CITATIONS BY SIZE CARD - Compare to similar-sized facilities
// ─────────────────────────────────────────────────────────────────────────────

const CitationsBySizeCard: React.FC<{
  facilityAvgCitations: number;
  facilityTotalCitations: number;
  numYears: number;
  beds: number | undefined;
  state: string;
  benchmarks: Record<string, Record<string, SizeBucketBenchmark>> | null | undefined;
}> = ({ facilityAvgCitations, facilityTotalCitations, numYears, beds, state, benchmarks }) => {
  if (!benchmarks) return null;

  const sizeBucket = getSizeBucket(beds);
  const nationalBenchmark = benchmarks.national?.[sizeBucket];
  const stateBenchmark = benchmarks[state]?.[sizeBucket];

  if (!nationalBenchmark) return null;

  // Determine percentile rank
  let percentileLabel = '';
  let severity: 'success' | 'warning' | 'error' = 'success';

  if (facilityAvgCitations > nationalBenchmark.p90) {
    percentileLabel = 'Worse than 90% of similar sized facilities';
    severity = 'error';
  } else if (facilityAvgCitations > nationalBenchmark.p75) {
    percentileLabel = 'Worse than 75% of similar sized facilities';
    severity = 'error';
  } else if (facilityAvgCitations > nationalBenchmark.median) {
    percentileLabel = 'Above median for similar sized facilities';
    severity = 'warning';
  } else {
    percentileLabel = 'Below median for similar sized facilities';
    severity = 'success';
  }

  // Build comparison items
  const comparisons = [
    {
      label: 'This Facility',
      value: facilityAvgCitations,
      isHighlighted: true,
      color: severity === 'error' ? 'error.main' : severity === 'warning' ? 'warning.main' : 'success.main',
    },
    {
      label: 'National Avg',
      value: nationalBenchmark.avg,
      color: '#90a4ae',
    },
    ...(stateBenchmark ? [{
      label: `${state} Avg`,
      value: stateBenchmark.avg,
      color: '#546e7a',
    }] : []),
  ].sort((a, b) => b.value - a.value);

  const maxValue = Math.max(...comparisons.map(c => c.value), 1) * 1.1;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 320,
        maxWidth: 400,
        p: 2.5,
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid',
        borderColor: severity === 'error' ? 'error.light' : 'divider',
      }}
    >
      {/* Title */}
      <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        Citations vs Similar-Sized Facilities
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        2020-2024 avg vs {nationalBenchmark.num_facilities.toLocaleString()} facilities ({nationalBenchmark.bed_range} beds)
      </Typography>

      {/* Key metric callout */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: severity === 'error' ? 'error.50' : severity === 'warning' ? 'warning.50' : 'success.50',
          borderRadius: 1,
          borderLeft: '4px solid',
          borderLeftColor: severity === 'error' ? 'error.main' : severity === 'warning' ? 'warning.main' : 'success.main',
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ color: severity === 'error' ? 'error.main' : severity === 'warning' ? 'warning.dark' : 'success.main' }}>
          {facilityAvgCitations.toFixed(1)} citations/year
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {percentileLabel} nationally
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {facilityTotalCitations} total citations (2020-2024)
        </Typography>
      </Box>

      {/* Bar chart */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {comparisons.map((item, idx) => {
          const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          return (
            <Box key={idx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                <Typography
                  variant="caption"
                  fontWeight={item.isHighlighted ? 700 : 500}
                  sx={{ color: item.isHighlighted ? item.color : 'text.secondary', fontSize: '0.7rem' }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: item.color, fontSize: '0.7rem' }}
                >
                  {item.value.toFixed(1)}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: item.isHighlighted ? 16 : 10,
                  backgroundColor: 'grey.200',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${barWidth}%`,
                    backgroundColor: item.color,
                    borderRadius: 1,
                    transition: 'width 0.5s ease-out',
                    minWidth: item.value > 0 ? 4 : 0,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TOTAL CITATIONS CARD - Raw summed citations (2019-2025) vs similar-sized facilities
// ─────────────────────────────────────────────────────────────────────────────

const TotalCitationsCard: React.FC<{
  facilityTotalCitations: number;
  beds: number | undefined;
  state: string;
  benchmarks: Record<string, Record<string, SizeBucketBenchmark>> | null | undefined;
}> = ({ facilityTotalCitations, beds, state, benchmarks }) => {
  if (!benchmarks) return null;

  const sizeBucket = getSizeBucket(beds);
  const nationalBenchmark = benchmarks.national?.[sizeBucket];
  const stateBenchmark = benchmarks[state]?.[sizeBucket];

  if (!nationalBenchmark || nationalBenchmark.sum_median == null) return null;

  const yearsLabel = nationalBenchmark.sum_years || '2019-2025';

  // Determine percentile rank using summed benchmarks
  let percentileLabel = '';
  let severity: 'success' | 'warning' | 'error' = 'success';

  if (nationalBenchmark.sum_p90 != null && facilityTotalCitations > nationalBenchmark.sum_p90) {
    percentileLabel = 'Worse than 90% of similar sized facilities';
    severity = 'error';
  } else if (nationalBenchmark.sum_p75 != null && facilityTotalCitations > nationalBenchmark.sum_p75) {
    percentileLabel = 'Worse than 75% of similar sized facilities';
    severity = 'error';
  } else if (facilityTotalCitations > nationalBenchmark.sum_median) {
    percentileLabel = 'Above median for similar sized facilities';
    severity = 'warning';
  } else {
    percentileLabel = 'Below median for similar sized facilities';
    severity = 'success';
  }

  // Build comparison items
  const comparisons = [
    {
      label: 'This Facility',
      value: facilityTotalCitations,
      isHighlighted: true,
      color: severity === 'error' ? 'error.main' : severity === 'warning' ? 'warning.main' : 'success.main',
    },
    {
      label: 'National Avg',
      value: nationalBenchmark.sum_avg ?? 0,
      color: 'info.main',
    },
    ...(stateBenchmark?.sum_avg != null ? [{
      label: `${state} Avg (${stateBenchmark.num_facilities} facilities)`,
      value: stateBenchmark.sum_avg,
      color: 'info.light',
    }] : []),
  ].sort((a, b) => b.value - a.value);

  const maxValue = Math.max(...comparisons.map(c => c.value), 1) * 1.1;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 280,
        p: 2.5,
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid',
        borderColor: severity === 'error' ? 'error.light' : 'divider',
      }}
    >
      <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        Total Citations
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {yearsLabel} vs {nationalBenchmark.num_facilities.toLocaleString()} facilities ({nationalBenchmark.bed_range} beds)
      </Typography>

      {/* Key metric callout */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: severity === 'error' ? 'error.50' : severity === 'warning' ? 'warning.50' : 'success.50',
          borderRadius: 1,
          borderLeft: '4px solid',
          borderLeftColor: severity === 'error' ? 'error.main' : severity === 'warning' ? 'warning.main' : 'success.main',
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ color: severity === 'error' ? 'error.main' : severity === 'warning' ? 'warning.dark' : 'success.main' }}>
          {facilityTotalCitations} citations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {percentileLabel} nationally
        </Typography>
      </Box>

      {/* Bar chart */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {comparisons.map((item, idx) => {
          const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          return (
            <Box key={idx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                <Typography
                  variant="caption"
                  fontWeight={item.isHighlighted ? 700 : 500}
                  sx={{ color: item.isHighlighted ? item.color : 'text.secondary', fontSize: '0.7rem' }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: item.color, fontSize: '0.7rem' }}
                >
                  {Math.round(item.value)}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: item.isHighlighted ? 16 : 10,
                  backgroundColor: 'grey.200',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${barWidth}%`,
                    backgroundColor: item.color,
                    borderRadius: 1,
                    transition: 'width 0.5s ease-out',
                    minWidth: item.value > 0 ? 4 : 0,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// F-TAG CATEGORY CARD - Show citation totals by category vs national average
// ─────────────────────────────────────────────────────────────────────────────

const FTagCategoryCard: React.FC<{
  facilityMetrics: PriorConductProps['facilityMetrics'];
  beds: number | undefined;
  benchmarks: Record<string, Record<string, SizeBucketBenchmark>> | null | undefined;
}> = ({ facilityMetrics, beds, benchmarks }) => {
  if (!benchmarks || !facilityMetrics) return null;

  const sizeBucket = getSizeBucket(beds);
  const nationalBenchmark = benchmarks.national?.[sizeBucket];

  if (!nationalBenchmark) return null;

  const numYears = 5; // 2020-2024

  // Get facility category TOTALS (not per-year)
  const facilityTotals = {
    quality_of_care: facilityMetrics.total_quality_of_care_ftags || 0,
    abuse_neglect: facilityMetrics.total_abuse_neglect_ftags || 0,
    infection_control: facilityMetrics.total_infection_control_ftags || 0,
    staffing: facilityMetrics.total_staffing_ftags || 0,
    pharmacy: facilityMetrics.total_pharmacy_ftags || 0,
    dietary: facilityMetrics.total_dietary_ftags || 0,
  };

  // Convert benchmark per-year averages to 5-year totals for comparison
  const nationalTotals = {
    quality_of_care: Math.round((nationalBenchmark.avg_quality_of_care_ftags || 0) * numYears),
    abuse_neglect: Math.round((nationalBenchmark.avg_abuse_neglect_ftags || 0) * numYears),
    infection_control: Math.round((nationalBenchmark.avg_infection_control_ftags || 0) * numYears),
    staffing: Math.round((nationalBenchmark.avg_staffing_ftags || 0) * numYears),
    pharmacy: Math.round((nationalBenchmark.avg_pharmacy_ftags || 0) * numYears),
    dietary: Math.round((nationalBenchmark.avg_dietary_ftags || 0) * numYears),
  };

  // Build comparison data - sorted by facility value descending
  const categories = [
    { label: 'Quality of Care', facility: facilityTotals.quality_of_care, national: nationalTotals.quality_of_care, color: 'primary.main' },
    { label: 'Abuse & Neglect', facility: facilityTotals.abuse_neglect, national: nationalTotals.abuse_neglect, color: 'error.main' },
    { label: 'Infection Control', facility: facilityTotals.infection_control, national: nationalTotals.infection_control, color: 'warning.main' },
    { label: 'Staffing', facility: facilityTotals.staffing, national: nationalTotals.staffing, color: 'secondary.main' },
    { label: 'Pharmacy', facility: facilityTotals.pharmacy, national: nationalTotals.pharmacy, color: 'info.main' },
    { label: 'Dietary', facility: facilityTotals.dietary, national: nationalTotals.dietary, color: 'success.main' },
  ]
    .filter(c => c.facility > 0 || c.national > 0) // Only show categories with data
    .sort((a, b) => b.facility - a.facility); // Sort by facility value descending

  // Check if facility has any F-tag data
  const totalFacilityFtags = Object.values(facilityTotals).reduce((sum, v) => sum + v, 0);
  if (totalFacilityFtags === 0 && categories.length === 0) return null;

  const maxValue = Math.max(...categories.map(c => Math.max(c.facility, c.national)), 1) * 1.1;

  // Count categories where facility exceeds national significantly
  const aboveBenchmarkCount = categories.filter(c => c.facility > c.national * 1.5).length;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 320,
        maxWidth: 400,
        p: 2.5,
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid',
        borderColor: aboveBenchmarkCount >= 2 ? 'warning.light' : 'divider',
      }}
    >
      {/* Title */}
      <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        Citations by Category
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Total citations 2020-2024 vs national avg for similar sized facilities
      </Typography>

      {/* Category bars */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {categories.map((cat, idx) => {
          const facilityWidth = maxValue > 0 ? (cat.facility / maxValue) * 100 : 0;
          const nationalWidth = maxValue > 0 ? (cat.national / maxValue) * 100 : 0;
          const isAboveNational = cat.facility > cat.national * 1.5;

          return (
            <Box key={idx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                <Typography
                  variant="caption"
                  fontWeight={500}
                  sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                >
                  {cat.label}
                </Typography>
                <Box display="flex" gap={1} alignItems="baseline">
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: isAboveNational ? 'warning.dark' : 'text.primary', fontSize: '0.75rem' }}
                  >
                    {cat.facility}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.65rem' }}
                  >
                    vs {cat.national} avg
                  </Typography>
                </Box>
              </Box>
              {/* Bar with national marker */}
              <Box sx={{ position: 'relative', height: 12, backgroundColor: 'grey.200', borderRadius: 1 }}>
                {/* Facility bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    height: '100%',
                    width: `${facilityWidth}%`,
                    backgroundColor: isAboveNational ? 'warning.main' : cat.color,
                    borderRadius: 1,
                    transition: 'width 0.5s ease-out',
                    minWidth: cat.facility > 0 ? 4 : 0,
                  }}
                />
                {/* National avg marker line */}
                {cat.national > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${nationalWidth}%`,
                      height: '100%',
                      width: 2,
                      backgroundColor: 'grey.600',
                      borderRadius: 0.5,
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box display="flex" gap={2} mt={1.5} justifyContent="center">
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 12, height: 8, backgroundColor: 'primary.main', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            This Facility
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 2, height: 10, backgroundColor: 'grey.600', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            National Avg
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPARISON CARD - Horizontal bar chart with prosecution baselines
// ─────────────────────────────────────────────────────────────────────────────

const ComparisonCard: React.FC<{
  title: string;
  subtitle?: string;
  facilityValue: number;
  sweetwaterValue?: number;
  njValue?: number;
  stateAvgValue: number;
  state: string;
  formatValue?: (val: number) => string;
  higherIsBad?: boolean;
}> = ({
  title,
  subtitle,
  facilityValue,
  sweetwaterValue,
  njValue,
  stateAvgValue,
  state,
  formatValue = (v) => v.toFixed(0),
  higherIsBad = true,
}) => {
  const hasSweetwater = sweetwaterValue != null;
  const hasNJ = njValue != null;

  // Determine severity
  const isWorseThanSweetwater = hasSweetwater && (higherIsBad ? facilityValue > sweetwaterValue : facilityValue < sweetwaterValue);
  const isWorseThanNJ = hasNJ && (higherIsBad ? facilityValue > njValue! : facilityValue < njValue!);
  const isAboveAvg = higherIsBad ? facilityValue > stateAvgValue : facilityValue < stateAvgValue;
  const isClean = facilityValue === 0;

  // Build comparison bars
  const comparisons = [
    { label: 'This Facility', value: facilityValue, isHighlighted: true, barColor: '' },
    ...(hasSweetwater ? [{ label: 'Sweetwater', value: sweetwaterValue, isHighlighted: false, barColor: '#1565c0' }] : []),
    ...(hasNJ ? [{ label: 'Deptford', value: njValue!, isHighlighted: false, barColor: '#7b1fa2' }] : []),
    { label: `${state} Average`, value: stateAvgValue, isHighlighted: false, barColor: '#546e7a' },
  ].sort((a, b) => b.value - a.value);

  const maxValue = Math.max(...comparisons.map((c) => c.value), 1) * 1.1;

  // Determine facility color based on severity
  const getFacilityColor = () => {
    if (isClean) return 'success.main';
    if (isWorseThanSweetwater) return '#b71c1c';
    if (isWorseThanNJ) return 'error.main';
    if (isAboveAvg) return 'warning.main';
    return 'success.main';
  };

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 280,
        p: 2.5,
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isWorseThanNJ ? 'error.light' : 'divider',
      }}
    >
      {/* Title */}
      <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}

      {/* Severity callout */}
      {isWorseThanSweetwater && !isClean && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            borderLeft: '4px solid #b71c1c',
          }}
        >
          <Typography variant="body2" fontWeight={700} color="#b71c1c">
            Exceeds Sweetwater
          </Typography>
        </Box>
      )}
      {!isWorseThanSweetwater && isWorseThanNJ && !isClean && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: '#fff3e0',
            borderRadius: 1,
            borderLeft: '4px solid',
            borderLeftColor: 'warning.main',
          }}
        >
          <Typography variant="body2" fontWeight={700} color="warning.dark">
            Exceeds Deptford
          </Typography>
        </Box>
      )}
      {/* Bar chart */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {comparisons.map((item, idx) => {
          const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          const barHeight = 14;
          let barColor: string;
          let labelColor: string;

          if (item.isHighlighted) {
            barColor = getFacilityColor();
            labelColor = barColor;
          } else {
            barColor = item.barColor;
            labelColor = 'text.secondary';
          }

          return (
            <Box key={idx}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography
                  variant="caption"
                  fontWeight={item.isHighlighted ? 700 : 500}
                  sx={{ color: item.isHighlighted ? labelColor : 'text.secondary' }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: item.isHighlighted ? labelColor : barColor }}
                >
                  {formatValue(item.value)}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: barHeight,
                  backgroundColor: 'grey.200',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${barWidth}%`,
                    backgroundColor: barColor,
                    borderRadius: 1,
                    transition: 'width 0.5s ease-out',
                    minWidth: item.value > 0 ? 4 : 0,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const PriorConduct: React.FC<PriorConductProps> = ({
  ccn,
  schemeId,
  facilityYears,
  state,
  beds,
  startYear: startYearProp,
  endYear: endYearProp,
  facilityMetrics,
  citationBenchmarks,
}) => {
  const [loading, setLoading] = useState(true);
  const [_events, setEvents] = useState<TimelineEvent[]>([]);
  const [totalFinesFromDb, setTotalFinesFromDb] = useState<number | null>(null);
  const [totals, setTotals] = useState({
    citations: 0,
    harm: 0,
    jeopardy: 0,
    fines: 0,
    inspections: 0,
  });

  // Get baselines for this state
  const baselines = STATE_BASELINES[state] || STATE_BASELINES.DEFAULT;

  // Fetch inspection data and penalty totals
  useEffect(() => {
    if (!ccn) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch timeline data and monthly-timeline (for accurate penalty totals) in parallel
        const [timelineRes, monthlyRes] = await Promise.all([
          api.get(`/facilities/${ccn}/inspections/`),
          api.get(`/facilities/${ccn}/monthly-timeline/`, {
            params: { start_year: startYearProp || 2019, end_year: endYearProp || new Date().getFullYear() },
          }).catch(() => ({ data: { summary: null } })),
        ]);

        if (cancelled) return;

        // Get accurate penalty total from monthly-timeline (queries analytics DB directly)
        if (monthlyRes.data?.summary?.total_fines != null) {
          setTotalFinesFromDb(monthlyRes.data.summary.total_fines);
        }

        const inspections = Array.isArray(timelineRes.data?.inspections)
          ? timelineRes.data.inspections
          : Array.isArray(timelineRes.data?.results)
            ? timelineRes.data.results
            : Array.isArray(timelineRes.data)
              ? timelineRes.data
              : [];

        const timelineEvents: TimelineEvent[] = inspections.map((insp: any) => ({
          id: insp.id,
          date: insp.inspection_date,
          year: new Date(insp.inspection_date).getFullYear(),
          type: insp.inspection_type?.toLowerCase() === 'complaint' ? 'complaint' : 'health',
          summary: `${insp.inspection_type === 'HEALTH' ? 'Health' : 'Complaint'} Inspection`,
          citations: insp.total_citations,
          harm_citations: insp.harm_citations || 0,
          jeopardy_citations: insp.jeopardy_citations || 0,
          details: (insp.citations || []).map((c: any) => ({
            ftag: c.ftag_display || c.ftag,
            description: c.ftag_description || '',
            severity: c.scope_severity_code || '',
            is_core: c.is_understaffing_core || false,
          })),
        }));

        // Add penalty/SFF events from facility years
        if (facilityYears) {
          facilityYears.forEach((fy: any) => {
            if (fy.total_fine_amount && fy.total_fine_amount > 0) {
              timelineEvents.push({
                id: `penalty-${fy.year}`,
                date: `${fy.year}-06-01`,
                year: fy.year,
                type: 'penalty',
                summary: 'Civil Money Penalty',
                fine_amount: Number(fy.total_fine_amount),
              });
            }
            if (fy.is_sff) {
              timelineEvents.push({
                id: `sff-${fy.year}`,
                date: `${fy.year}-01-01`,
                year: fy.year,
                type: 'sff',
                summary: 'Special Focus Facility',
              });
            }
          });
        }

        timelineEvents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setEvents(timelineEvents);

        setTotals({
          citations: timelineEvents.reduce((sum, e) => sum + (e.citations || 0), 0),
          harm: timelineEvents.reduce((sum, e) => sum + (e.harm_citations || 0), 0),
          jeopardy: timelineEvents.reduce((sum, e) => sum + (e.jeopardy_citations || 0), 0),
          fines: timelineEvents.filter((e) => e.type === 'penalty').reduce((sum, e) => sum + (e.fine_amount || 0), 0),
          inspections: timelineEvents.filter((e) => e.type === 'health' || e.type === 'complaint').length,
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching prior conduct:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [ccn, schemeId, startYearProp, endYearProp]);

  // Calculate avg citations/year for size-based comparison (2020-2024 = 5 years)
  const totalCitations = facilityMetrics?.total_citations ?? totals.citations;
  // Use 5 years (2020-2024) to match benchmarks - facilityYears may have fewer if facility is newer
  const numYears = Math.min(facilityYears?.length || 5, 5);
  const avgCitationsPerYear = totalCitations ? totalCitations / numYears : 0;

  // Use penalty total from analytics DB (most accurate), fall back to facility metrics or timeline totals
  const totalFines = totalFinesFromDb ?? facilityMetrics?.total_fine_amount ?? totals.fines;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      {/* Left column — Inspection History (3/4 width) */}
      <Box sx={{ flex: 3, minWidth: 0 }}>
        <MonthlyTimeline ccn={ccn} startYear={startYearProp} endYear={endYearProp} hideChart />
      </Box>

      {/* Right column — Comparison cards (1/4 width) */}
      <Box sx={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TotalCitationsCard
          facilityTotalCitations={totalCitations}
          beds={beds}
          state={state}
          benchmarks={citationBenchmarks}
        />
        <ComparisonCard
          title="Civil Money Penalties"
          subtitle="Fines imposed by CMS"
          facilityValue={totalFines}
          stateAvgValue={baselines.total_fines}
          state={state}
          formatValue={formatMoney}
        />
      </Box>
    </Box>
    </Box>
  );
};

export default PriorConduct;
